import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { combineLatest, distinctUntilChanged, filter, finalize, forkJoin, skip, Subscription } from 'rxjs';
import { TimespanPipe } from 'src/app/pipes/timespan/timespan.pipe';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { TextDividerComponent } from 'src/app/shared/components/text-divider/text-divider.component';
import { getAllIslandLocations, getAllRoyalMarketLocations } from 'src/app/shared/constants/locations.constant';
import { getAllPriceTypes, getAllSalesTaxes, PRICE_TYPES, SALES_TAXES, SETUP_FEES } from 'src/app/shared/constants/market.constant';
import { Achievement } from 'src/app/shared/models/achievement.model';
import { MarketHistoriesResponse, MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { Biome, ClusterQuality, FarmPlot } from 'src/app/shared/models/farming-models/farm-models';
import { Item } from 'src/app/shared/models/items.model';
import { SalesTax, SetupFee } from 'src/app/shared/models/market.model';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AfmBeService } from 'src/app/services/afm-be/afm-be.service';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { RouterModule } from '@angular/router';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { FeatureComponent } from 'src/app/shared/components/feature/feature.component';
import { SubsectionComponent } from 'src/app/shared/components/subsection/subsection.component';
import { IntroContainerComponent } from 'src/app/shared/components/intro-container/intro-container.component';
import { SectionSubstitleComponent } from 'src/app/shared/components/section-subtitle/section-subtitle.component';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';

@Component({
  selector: 'app-farm-calculator',
  imports: [
    CommonModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    TextDividerComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatInputModule,
    MatTableModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    MatIconModule,
    FormsModule,
    TimespanPipe,
    MatGridListModule,
    MatPaginatorModule,
    MatRadioModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    RouterModule,
    SectionComponent,
    FeatureComponent,
    SubsectionComponent,
    IntroContainerComponent,
    SectionSubstitleComponent,
    ItemImageComponent,
  ],
  templateUrl: './farm-calculator.component.html',
  styleUrl: './farm-calculator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FarmCalculatorComponent implements OnInit, AfterViewInit, OnDestroy {
  //table
  get displayedColumns(): string[] {
    const result = ['plotInput', 'outputs', 'totalExpenses', 'totalIncome', 'profit', 'duration', 'profitPerDay'];
    if (this.userSettingsService.getUserFarmConfig().useFocus) {
      result.push('maxFocus', 'profitPerFocus');
    }

    if (!this.userSettingsService.getUserFarmConfig().hideFameData) {
      result.push('plotFame', 'famePerDay');
    }

    if (this.userSettingsService.getUserFarmConfig().useFocus && !this.userSettingsService.getUserFarmConfig().hideFameData) {
      result.push('famePerFocus');
    }

    return result;
  }
  readonly dataSource = new MatTableDataSource<FarmPlot>([]);
  private _sort!: MatSort;
  private _paginator!: MatPaginator;

  @ViewChild(MatSort)
  set sort(sort: MatSort) {
    this._sort = sort;
    this.setDataSourceAttributes();
  }
  get sort(): MatSort {
    return this._sort;
  }

  @ViewChild(MatPaginator)
  set paginator(paginator: MatPaginator) {
    this._paginator = paginator;
    this.setDataSourceAttributes();
  }
  get paginator(): MatPaginator {
    return this._paginator;
  }

  private setDataSourceAttributes() {
    if (this.dataSource) {
      this.dataSource.sort = this._sort;
      this.dataSource.paginator = this._paginator;

      this.dataSource.filterPredicate = (data: FarmPlot, filter: string) => {
        const filterValue = this.filter.trim().toLowerCase();

        const hideMissingPrices = this.userSettingsService.getUserFarmConfig().hideMissingPrices;
        const selectedPlotKinds = this.userSettingsService.getUserFarmConfig().selectedPlotKinds;
        return (
          (!hideMissingPrices || !data.hasMissingPrices) &&
          (data.plotInput.item.localizedName.toLowerCase().includes(filterValue) || data.outputs.some((output) => output.item.localizedName.toLowerCase().includes(filterValue))) &&
          (selectedPlotKinds.length === 0 || selectedPlotKinds.includes(data.plotInput.item.farmHarvest ? 'Harvest' : data.plotInput.item.farmGrownItem ? 'Grow' : data.plotInput.item.farmProducts ? 'Products' : ''))
        );
      };

      this.dataSource.sortingDataAccessor = (item: FarmPlot, property) => {
        switch (property) {
          case 'plotInput':
            return item.plotInput.item.localizedName;
          case 'totalExpenses':
            return item.expenses;
          case 'totalIncome':
            return item.income;
          case 'profit':
            return item.profit;
          case 'maxFocus':
            return item.actionMaxFocusCost;
          case 'profitPerFocus':
            return item.profitPerFocus;
          case 'duration':
            return item.realFarmDurationSeconds;
          case 'profitPerDay':
            return item.profitPerDay;
          case 'plotFame':
            return item.plotFame;
          case 'famePerDay':
            return item.famePerDay;
          case 'famePerFocus':
            return item.famePerFocus;
          default:
            return item.profit;
        }
      };
    }
  }

  //others
  subscriptions: Subscription[] = [];
  userCanAccess: Signal<boolean>;

  //filter
  filter: string = '';

  //market
  readonly marketHistories: MarketHistoriesResponse[] = [];
  readonly marketPrices: MarketResponse[] = [];
  islandLocations = getAllIslandLocations();
  marketLocations = getAllRoyalMarketLocations();
  priceTypes = getAllPriceTypes();
  PRICE_TYPES = PRICE_TYPES;
  taxes = getAllSalesTaxes();
  SALES_TAXES = SALES_TAXES;
  get salesTax(): SalesTax {
    return this.userSettingsService.getUserFarmConfig().hasPremium ? SALES_TAXES.premium : SALES_TAXES.normal;
  }
  get setupFee(): SetupFee {
    return this.userSettingsService.getUserFarmConfig().hasPremium ? SETUP_FEES.normal : SETUP_FEES.normal;
  }
  enableUpdatePricesButton: boolean = true;

  //farming
  plotItemsUniqueNames: string[] = [];
  farmPlots: FarmPlot[] = [];
  biomes = Object.values(Biome);
  clusterQualities = Object.values(ClusterQuality);
  achievementsSet: Achievement[] = [];
  plotKinds: string[] = ['Harvest', 'Grow', 'Products'];

  //user
  disableSaveConfig: boolean = false;

  constructor(
    public userSettingsService: UserSettingsService,
    private jsonReadService: JsonReaderService,
    private aoDataGetter: AoDataGetterService,
    public cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    public afmBeService: AfmBeService,
    public authService: AuthService,
    public imageService: AlbionImageService
  ) {
    this.userCanAccess = computed(() => {
      return this.afmBeService.afmStripeUserData().entitlementsLookups.includes('flipper-masterpiece') || this.afmBeService.afmStripeUserData().entitlementsLookups.includes('flipper-outstanding');
    });
  }

  ngOnInit(): void {
    this.jsonReadService.ensureLoaded();
  }

  ngAfterViewInit() {
    //JSONREADER
    this.subscriptions.push(
      combineLatest([this.jsonReadService.achievementsData$, this.jsonReadService.itemsData$, this.jsonReadService.farmingModifiersData$, this.jsonReadService.hasLoaded$])
        .pipe(filter(([achievements, items, farmingModifiers, hasLoaded]) => !!achievements && !!items && !!farmingModifiers && hasLoaded))
        .subscribe(([achievements, items, farmingModifiers, hasLoaded]) => {
          const getSortPriority = (item: any): number => {
            if (item.farmHarvest) return 1;
            if (item.farmGrownItem) return 2;
            if (item.products) return 3;
            return 4;
          };
          const baseInputs = items.filter((item) => !item.unlockedToPlace && (item.farmHarvest || item.farmGrownItem || item.farmProducts)).sort((a, b) => getSortPriority(a) - getSortPriority(b));

          const foodItems = items.filter((item) => item.foodCategory);

          this.farmPlots = baseInputs.map((item) => {
            const farmingModifiersForItem = farmingModifiers.filter((modifier) => modifier.farmingYieldModifier.filter((x) => x.uniqueName === item.uniqueName).length > 0);
            const baseFame = !!item.farmHarvest ? item.farmHarvest.fame : !!item.farmGrownItem ? item.farmGrownItem.fame : !!item.farmProducts ? item.farmProducts.product.fame : 0;
            return new FarmPlot(item, achievements, this.userSettingsService, foodItems, this.marketPrices, this.marketHistories, this.aoDataGetter, farmingModifiersForItem, baseFame);
          });

          let set = new Set<string>();
          this.farmPlots.forEach((plot) => {
            set.add(plot.plotInput.item.uniqueName);
            plot.outputs.forEach((output) => {
              set.add(output.item.uniqueName);
            });
            plot.nutritionInputs.forEach((input) => {
              set.add(input.item.uniqueName);
            });
          });
          this.plotItemsUniqueNames = Array.from(set);

          let achiSet = new Set<Achievement>();
          this.farmPlots.forEach((plot) => {
            plot.achievements.forEach((achi) => achiSet.add(achi));
          });
          this.achievementsSet = Array.from(achiSet);

          this.updatePrices(true);

          this.dataSource.data = this.farmPlots;

          this.cdr.markForCheck();
        })
    );

    //AFMUSER
    this.subscriptions.push(
      this.userSettingsService.afmUser$.pipe(distinctUntilChanged()).subscribe((user) => {
        this.farmPlots.forEach((plot) => plot.setUserAchievementsLevels());
        this.calculateAllPlots(false);
        this.runFilter();

        this.cdr.markForCheck();
      })
    );

    //USERSETTINGS
    this.subscriptions.push(
      this.userSettingsService.userServer$.pipe(skip(1), distinctUntilChanged()).subscribe((server) => {
        this.updatePrices();

        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  saveSpec(id: string, level: number) {
    this.userSettingsService.setUserSpec(id, level);
  }

  async updatePrices(selectCheapestNutrition: boolean = false) {
    if (this.plotItemsUniqueNames.length === 0) {
      return;
    }

    this.enableUpdatePricesButton = false;
    forkJoin({
      prices: this.aoDataGetter.getPrices(this.plotItemsUniqueNames),
      history: this.aoDataGetter.getHistory(this.plotItemsUniqueNames),
    })
      .pipe(
        finalize(() => {
          this.enableUpdatePricesButton = true;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ prices, history }) => {
          this.marketPrices.splice(0, this.marketPrices.length, ...prices);
          this.marketHistories.splice(0, this.marketHistories.length, ...history);

          this.calculateAllPlots(true);

          if (selectCheapestNutrition) {
            this.selectAllCheapestNutritions();
          }
        },
        error: (error) => {
          console.error('Error fetching market data:', error);
        },
      });
  }

  calculateAllPlots(resetChosenPrices: boolean) {
    this.farmPlots.forEach((plot) => plot.calculate(resetChosenPrices));
  }

  calculatePlot(plot: FarmPlot, resetChosenPrices: boolean) {
    plot.calculate(resetChosenPrices);
  }

  calculatePlotsWithAchievement(achievement: Achievement) {
    this.farmPlots.filter((plot) => plot.achievements.includes(achievement)).forEach((plot) => plot.calculate(false));
  }

  selectAllCheapestNutritions() {
    this.farmPlots.forEach((plot) => plot.selectCheapestNutrition());
  }

  swapBuyFromNpc(buyFromNpc: boolean) {
    this.farmPlots
      .filter((x) => (x.plotInput.npcPrice ?? 0) > 0)
      .forEach((plot) => {
        plot.plotInput.setNpcPrice();
        plot.plotInput.setPrice();
        plot.plotInput.resetChosenPrice();
        plot.calculate(false);
      });
  }

  updateGlobalDiscounts() {
    this.farmPlots
      .filter((x) => (x.plotInput.npcPrice ?? 0) > 0)
      .forEach((plot) => {
        plot.plotInput.setNpcPrice();
        plot.plotInput.setPrice();
        plot.plotInput.resetChosenPrice();
        plot.calculate(false);
      });
  }

  runFilter() {
    this.dataSource.filter = 'asdf';
  }

  async saveFarmConfiguration(): Promise<void> {
    if (this.userSettingsService.isLoggedIn) {
      this.disableSaveConfig = true;

      await this.userSettingsService.updateUserFarmConfig(this.userSettingsService.getUserFarmConfig(), this.userSettingsService.getUserSpecMap());

      setTimeout(() => {
        this.disableSaveConfig = false;
      }, 5000);
    } else {
      this.snackBar.open(`You must be Logged In to save!`, undefined, { duration: 2000 });
    }
  }

  setFoodPrice(food: Item, price: number) {
    // Find all plots that use this food
    const plotsUsingFood = this.farmPlots.filter((plot) => plot.nutritionInputs.some((nutrition) => nutrition.item.uniqueName === food.uniqueName));

    // For each plot found, update the chosen price and recalculate
    plotsUsingFood.forEach((plot) => {
      const nutritionItem = plot.nutritionInputs.find((n) => n.item.uniqueName === food.uniqueName);
      if (nutritionItem) {
        nutritionItem.chosenPrice = price;
        this.calculatePlot(plot, false);
      }
    });
  }

  setOutputPrice(output: Item, price: number) {
    const plotsUsingOutput = this.farmPlots.filter((plot) => plot.outputs.some((o) => o.item.uniqueName === output.uniqueName));
    plotsUsingOutput.forEach((plot) => {
      const outputItem = plot.outputs.find((o) => o.item.uniqueName === output.uniqueName);
      if (outputItem) {
        outputItem.chosenPrice = price;
        this.calculatePlot(plot, false);
      }
    });
  }
}
